import {
  DateRangeFilter,
  IMeasureMetadata,
  MeasureFormatter,
  MeasureIcon,
  MeasureReducer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DataAccessor,
  FilterAccessor,
  ICanBeChartedProperty,
  ICanDoAllProperty,
  ICanGroupMeasuresProperty,
  ICanQueryByTimestampProperty,
  IGroupByDimensionMetadata,
  IGroupedDimension,
  IMeasure,
  IMeasureBuilderData,
  IMeasureFilter,
  IMeasureRef,
  IReduceByMultiplier,
  IReduceByRate,
  isCanBeChartedProperty,
  ReduceByFunction,
} from '@principle-theorem/reporting/interfaces';
import { AtLeast, uid } from '@principle-theorem/shared';
import { ComparableObject, ComparableValue, Dimension } from 'crossfilter2';
import { cloneDeep, isBoolean, isEqual, uniqWith } from 'lodash';
import { BigQuerySQL } from '../big-query-sql';
import { getCustomReportFilterSQL } from '../querying';
import { NullMeasureFilter } from './measure-filters';

export function toMeasureBuilderData(
  measure: CanBeChartedProperty,
  reducer?: MeasureReducer,
  label?: string,
  filters?: ComparableValue[]
): IMeasureBuilderData {
  return { uid: uid(), measure, label, reducer, filters };
}

export function noFilterAccessor(): FilterAccessor {
  return (_filter) => undefined;
}

export function basicFilterAccessor(
  dataPropertyPath: string,
  convertValuesToNull: (string | number | DateRangeFilter)[] = []
): FilterAccessor {
  return (filter, context) =>
    getCustomReportFilterSQL(
      dataPropertyPath,
      filter,
      context,
      convertValuesToNull
    );
}

export function propertyExistsFilterAccessor(
  dataPropertyPath: string
): FilterAccessor {
  return (filter) =>
    BigQuerySQL.or(
      filter.values.map((filterValue) =>
        new NullMeasureFilter(!filterValue.value).value({
          propertyName: dataPropertyPath,
        })
      )
    );
}

export function stringPropertyHasValueFilterAccessor(
  dataPropertyPath: string
): FilterAccessor {
  return (filter) =>
    BigQuerySQL.or(
      filter.values.map((filterValue) => {
        const isEmpty = BigQuerySQL.or([
          BigQuerySQL.filter(dataPropertyPath, 'IS', undefined),
          BigQuerySQL.filter(dataPropertyPath, '=', ''),
        ]);
        const isNotEmpty = BigQuerySQL.and([
          BigQuerySQL.filter(dataPropertyPath, 'IS NOT', undefined),
          BigQuerySQL.filter(dataPropertyPath, '!=', ''),
        ]);
        return filterValue.value ? isNotEmpty : isEmpty;
      })
    );
}

export function numberPropertyHasValueFilterAccessor(
  dataPropertyPath: string
): FilterAccessor {
  return (filter) =>
    BigQuerySQL.or(
      filter.values.map((filterValue) => {
        const isEmpty = BigQuerySQL.or([
          BigQuerySQL.filter(dataPropertyPath, 'IS', undefined),
          BigQuerySQL.filter(dataPropertyPath, '=', 0),
        ]);
        const isNotEmpty = BigQuerySQL.and([
          BigQuerySQL.filter(dataPropertyPath, 'IS NOT', undefined),
          BigQuerySQL.filter(dataPropertyPath, '!=', 0),
        ]);
        return filterValue.value ? isNotEmpty : isEmpty;
      })
    );
}

export function booleanPropertyFilterAccessor(
  dataPropertyPath: string,
  isUndefinedTruthy: boolean = false
): FilterAccessor {
  return (filter) =>
    BigQuerySQL.or(
      filter.values.map((filterValue) => {
        const isUndefinedOrFalse = BigQuerySQL.or([
          BigQuerySQL.filter(dataPropertyPath, 'IS', undefined),
          BigQuerySQL.filter(dataPropertyPath, '=', false),
        ]);
        const isDefinedAndFalse = BigQuerySQL.and([
          BigQuerySQL.filter(dataPropertyPath, 'IS NOT', undefined),
          BigQuerySQL.filter(dataPropertyPath, '=', false),
        ]);
        const isUndefinedOrTrue = BigQuerySQL.or([
          BigQuerySQL.filter(dataPropertyPath, 'IS', undefined),
          BigQuerySQL.filter(dataPropertyPath, '=', true),
        ]);
        const isDefinedAndTrue = BigQuerySQL.and([
          BigQuerySQL.filter(dataPropertyPath, 'IS NOT', undefined),
          BigQuerySQL.filter(dataPropertyPath, '=', true),
        ]);

        const isFalsy = isUndefinedTruthy
          ? isDefinedAndFalse
          : isUndefinedOrFalse;
        const isTruthy = isUndefinedTruthy
          ? isUndefinedOrTrue
          : isDefinedAndTrue;

        return filterValue.value ? isTruthy : isFalsy;
      })
    );
}

export function getFormatterDefaultValue(
  formatter: MeasureFormatter
): ComparableValue {
  const defaultValueMap: Record<MeasureFormatter, ComparableValue> = {
    [MeasureFormatter.Boolean]: false,
    [MeasureFormatter.Currency]: 0,
    [MeasureFormatter.Custom]: '',
    [MeasureFormatter.Day]: 0,
    [MeasureFormatter.Link]: '',
    [MeasureFormatter.Hours]: 0,
    [MeasureFormatter.Minutes]: 0,
    [MeasureFormatter.Month]: 0,
    [MeasureFormatter.Number]: 0,
    [MeasureFormatter.Percentage]: 0,
    [MeasureFormatter.Prefix]: '',
    [MeasureFormatter.Suffix]: '',
    [MeasureFormatter.Text]: '',
    [MeasureFormatter.Time]: '',
    [MeasureFormatter.Timestamp]: '',
  };
  return defaultValueMap[formatter];
}

export class CanBeChartedProperty implements ICanBeChartedProperty {
  public metadata: IMeasureMetadata;
  public measure: IMeasure;

  constructor(data: AtLeast<ICanBeChartedProperty, 'metadata' | 'measure'>) {
    this.metadata = data.metadata;
    this.measure = data.measure;
    if (this.measure.reduceBy) {
      this.reduceByCount();
    }
  }

  clone(): CanBeChartedProperty {
    return new CanBeChartedProperty({
      metadata: cloneDeep(this.metadata),
      measure: cloneDeep(this.measure),
    });
  }

  setMetadata(metadata: Partial<IMeasureMetadata>): this {
    this.metadata = { ...this.metadata, ...metadata };
    return this;
  }

  setLabel(label: string): this {
    this.metadata.label = label;
    return this;
  }

  setIcon(icon: MeasureIcon): this {
    this.metadata.icon = icon;
    return this;
  }

  setFormatter(
    formatter: MeasureFormatter,
    formatterValue?: IMeasureMetadata['formatterValue']
  ): this {
    this.metadata.formatter = formatter;
    if (formatterValue) {
      this.metadata.formatterValue = formatterValue;
    }
    return this;
  }

  groupBy(groupBy: ICanGroupMeasuresProperty): this {
    this.measure.query.attributes.push(...groupBy.groupMeasure.queryAttributes);
    if (isCanBeChartedProperty(groupBy)) {
      if (!this.measure.query.joins) {
        this.measure.query.joins = [];
      }
      this.measure.query.joins = uniqWith(
        [...this.measure.query.joins, ...(groupBy.measure.query.joins ?? [])],
        isEqual
      );
    }
    return this;
  }

  filterBy(filter: IMeasureFilter, measure: IMeasure = this.measure): this {
    const query: string = filter.value(measure);
    if (!this.measure.query.filters) {
      this.measure.query.filters = [];
    }
    this.measure.query.filters.push(query);
    return this;
  }

  reduceBySum(): this {
    this.measure.reduceBy = (
      dimension: Dimension<unknown, ComparableObject>
    ) => {
      return (
        dimension
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .group<any, ComparableObject>()
          .reduceSum((fact) =>
            parseFloat(this.measure.dataAccessor(fact)?.toString() ?? '0')
          )
      );
    };
    return this;
  }

  reduceByCount(): this {
    this.metadata.formatter = MeasureFormatter.Number;
    this.measure.reduceBy = (
      dimension: Dimension<unknown, ComparableObject>
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return dimension.group<any, ComparableObject>().reduceCount();
    };
    return this;
  }

  reduceByAverage(): this {
    return this.reduceByRatio();
  }

  reduceByMultiplier(multiplier: ICanBeChartedProperty): this {
    let multiplierAccessor: DataAccessor | undefined;
    if (multiplier && multiplier.measure.dataAccessor) {
      multiplierAccessor = multiplier.measure.dataAccessor;
    }
    this.measure.reduceBy = (
      dimension: Dimension<unknown, ComparableObject>
    ) => {
      return dimension.group<IGroupedDimension, IReduceByMultiplier>().reduce(
        (data: IReduceByMultiplier, facts) => {
          ++data.count;
          data.multiplicand += parseFloat(
            this.measure.dataAccessor(facts).toString()
          );
          data.multiplier += multiplierAccessor
            ? parseFloat(multiplierAccessor(facts).toString())
            : data.count;
          data.valueOf = () => data.multiplicand * data.multiplier || 0;
          return data;
        },
        (data: IReduceByMultiplier, facts) => {
          --data.count;
          data.multiplicand -= parseFloat(
            this.measure.dataAccessor(facts).toString()
          );
          data.multiplier -= multiplierAccessor
            ? parseFloat(multiplierAccessor(facts).toString())
            : data.count;
          data.valueOf = () => data.multiplicand * data.multiplier || 0;
          return data;
        },
        () => {
          return {
            count: 0,
            multiplicand: 0,
            multiplier: 0,
            valueOf: () => 0,
          };
        }
      );
    };
    return this;
  }

  reduceByRatio(denominator?: ICanBeChartedProperty): this {
    let denominatorAccessor: DataAccessor | undefined;
    if (denominator && denominator.measure.dataAccessor) {
      denominatorAccessor = denominator.measure.dataAccessor;
    }
    this.measure.reduceBy = (
      dimension: Dimension<unknown, ComparableObject>
    ) => {
      return dimension.group<IGroupedDimension, IReduceByRate>().reduce(
        (data: IReduceByRate, facts) => {
          ++data.count;
          data.numerator += booleanSafeParseFloat(
            this.measure.dataAccessor(facts)
          );
          if (denominatorAccessor) {
            data.denominator += booleanSafeParseFloat(
              denominatorAccessor(facts)
            );
          } else {
            data.denominator = data.count;
          }
          data.valueOf = () => data.numerator / data.denominator || 0;
          return data;
        },
        (data: IReduceByRate, facts) => {
          --data.count;
          data.numerator -= booleanSafeParseFloat(
            this.measure.dataAccessor(facts)
          );
          if (denominatorAccessor) {
            data.denominator -= booleanSafeParseFloat(
              denominatorAccessor(facts)
            );
          } else {
            data.denominator = data.count;
          }
          data.valueOf = () => data.numerator / data.denominator || 0;
          return data;
        },
        () => {
          return {
            count: 0,
            numerator: 0,
            denominator: 0,
            valueOf: () => 0,
          };
        }
      );
    };
    return this;
  }

  reduceByCustom(reducerFn: ReduceByFunction): this {
    this.measure.reduceBy = reducerFn;
    return this;
  }

  reduceBy(reducer: MeasureReducer): this {
    switch (reducer) {
      case MeasureReducer.Sum:
        return this.reduceBySum();
      case MeasureReducer.Average:
        return this.reduceByAverage();
      case MeasureReducer.Count:
        return this.reduceByCount();
      default:
        return this;
    }
  }
}

function booleanSafeParseFloat(value: unknown): number {
  return isBoolean(value) ? Number(value) : parseFloat(String(value));
}

export class CanDoAllProperty
  extends CanBeChartedProperty
  implements ICanDoAllProperty
{
  public groupMeasure: IGroupByDimensionMetadata;

  constructor(
    data: AtLeast<ICanDoAllProperty, 'metadata' | 'measure' | 'groupMeasure'>
  ) {
    super(data);
    this.groupMeasure = data.groupMeasure;
  }

  override clone(): CanDoAllProperty {
    return new CanDoAllProperty({
      metadata: cloneDeep(this.metadata),
      measure: cloneDeep(this.measure),
      groupMeasure: cloneDeep(this.groupMeasure),
    });
  }
}

export class CanGroupMeasuresProperty implements ICanGroupMeasuresProperty {
  public metadata: IMeasureMetadata;
  public groupMeasure: IGroupByDimensionMetadata;

  constructor(data: ICanGroupMeasuresProperty) {
    this.metadata = data.metadata;
    this.groupMeasure = data.groupMeasure;
  }
}

export class CanQueryByTimestampProperty
  extends CanDoAllProperty
  implements ICanQueryByTimestampProperty
{
  queryBy: IMeasureRef;

  constructor(
    data: AtLeast<
      ICanQueryByTimestampProperty,
      'metadata' | 'measure' | 'queryBy' | 'groupMeasure'
    >
  ) {
    super(data);
    this.queryBy = data.queryBy;
  }
}
